// editions.ts
import { ProtocolNameEnum } from 'src/api';

export const SUPPORTED_EDITIONS = ['wrScrm', 'aflScore', 'polyglot'] as const;
export type Edition = typeof SUPPORTED_EDITIONS[number];

export interface ProtocolOption {
    label: string;
    id: number;
    name: ProtocolNameEnum;
}

// Simplified protocol options map
export const protocolOptionMap = {
    polyglot: [
        {
            label: 'World Rugby',
            id: 1,
            name: ProtocolNameEnum.worldRugbyHiaProtocol,
        },
        {
            label: 'World Rugby Sevens',
            id: 2,
            name: ProtocolNameEnum.worldRugbySevensHiaProtocol,
        },
        {
            label: 'AFL',
            id: 3,
            name: ProtocolNameEnum.aflProtocol,
        }
    ],
    wrScrm: [
        {
            label: 'World Rugby',
            id: 1,
            name: ProtocolNameEnum.worldRugbyHiaProtocol,
        },
        {
            label: 'World Rugby Sevens',
            id: 2,
            name: ProtocolNameEnum.worldRugbySevensHiaProtocol,
        }
    ],
    aflScore: [
        {
            label: 'AFL',
            id: 3,
            name: ProtocolNameEnum.aflProtocol,
        }
    ]
} as const;

export interface EditionConfig {
    name: string;
    features: {
        teamLogo: boolean;
        playerImage: boolean;
        multiUnion: boolean;
        homeGraphs: boolean;
        chooseTeamProtocol: boolean,
        orgName: string,
        lowerCaseOrgName: string,
        pluralOrgName: string,
    };
    routeFilter: Array<string>,
    branding: {
        primaryColor: string;
        logoPath: string;
        logoHorizontalPath: string;
        playerDefaultPath: string;
        placeholderPath: string;
    };
    appLinks: {
        playStoreQr: string;
        appStoreQr: string;
        playStore: string;
        appStore: string;
    }
}

interface EditionsConfigMap {
    [key: string]: EditionConfig;
}

export const editionConfig: EditionsConfigMap = {
    polyglot: {
        name: 'Polyglot Dev',
        features: {
            teamLogo: true,
            playerImage: true,
            multiUnion: true,
            homeGraphs: true,
            chooseTeamProtocol: true,
            orgName: 'Union',   
            lowerCaseOrgName: 'union',
            pluralOrgName: 'Unions',   
        },
        routeFilter: [],
        branding: {
            primaryColor: '#2D8626',
            logoPath: '/polyglot-logo.svg',
            logoHorizontalPath: '/polyglot-logo-horizontal.svg',
            playerDefaultPath: '/wrScrm-player-default.webp',
            placeholderPath: '/polyglot-logo.svg',
        },
        appLinks: {
            playStore: 'https://play.google.com/store/apps',
            playStoreQr: '/polyglot-google-play-qr.gif',
            appStoreQr: '/polyglot-app-store-qr.gif',
            appStore: 'https://apps.apple.com/app/testflight/id899247664',
        }
    },
    wrScrm: {
        name: 'World Rugby SCRM',
        features: {
            teamLogo: true,
            playerImage: true,
            multiUnion: true,
            homeGraphs: true,
            chooseTeamProtocol: true,
            orgName: 'Union',
            lowerCaseOrgName: 'union',
            pluralOrgName: 'Unions', 
        },
        routeFilter: [],
        branding: {
            primaryColor: '#2D8626',
            logoPath: '/wrScrm-logo.svg',
            logoHorizontalPath: '/wrScrm-logo-horizontal.png',
            playerDefaultPath: '/wrScrm-player-default.webp',
            placeholderPath: '/wrScrm-placeholder.png',
        },
        appLinks: {
            playStore: 'https://play.google.com/store/apps/details?id=com.worldrugby.scrm',
            playStoreQr: '/google-play-2d.gif',
            appStoreQr: '/app-store-2d.gif',
            appStore: 'https://apps.apple.com/app/scrm/id1632354074',
        }
    },
    aflScore: {
        name: 'AFL Score',
        features: {
            teamLogo: true,
            playerImage: true,
            multiUnion: false,
            homeGraphs: false,
            chooseTeamProtocol: false,
            orgName: 'Tournament Tier',
            lowerCaseOrgName: 'tournament tier',
            pluralOrgName: 'Tournament Tiers'
        },
        routeFilter: ['comprehensives', 'print', 'iccreviews', 'statistics'],
        branding: {
            primaryColor: '#E21E26',
            logoPath: '/aflScore-logo.svg',
            logoHorizontalPath: '/aflScore-logo-horizontal.svg',
            playerDefaultPath: '/aflScore-player-default.png',
            placeholderPath: '/aflScore-logo.svg',
        },
        appLinks: {
            playStore: 'https://play.google.com/store/apps/details?id=rugby.world.aflscore',
            playStoreQr: '/aflScore-google-play-qr.gif',
            appStoreQr: '/aflScore-app-store-qr.gif',
            appStore: 'https://apps.apple.com/app/afl-score/id6739550209',
        }
    }
} as const;

const state = {
    edition: process.env.EDITION as Edition,
};

export function isRouteFiltered(route: string) {
    return getCurrentEdition().routeFilter.includes(route);
}

export function getCurrentEdition() {
    return {
        ...editionConfig[state.edition],
        edition: state.edition,
        protocolOptions: protocolOptionMap[state.edition],
    } as const;
}

export function isValidEdition(edition: string): edition is Edition {
    return SUPPORTED_EDITIONS.includes(edition as Edition);
}

// the function name hopefully makes it clear not to do this at runtime
// when the app is launched
// the admin portal only supports a singular EDITION at runtime in production
// there are too many unknowns around data and interactions with systems
// to support multiple editions at runtime simultaneously, currently
export function setEditionForTesting(edition: Edition) {
    state.edition = edition;
}